
import React, { useState } from 'react';
import { Wine, WineType } from '../types';

interface CellarViewProps {
  wines: Wine[];
  onRemove: (id: string) => void;
  onUpdateQty: (id: string, delta: number) => void;
  onUpdateWine: (id: string, updates: Partial<Wine>) => void;
  onViewToggle: (isDetail: boolean) => void;
}

const CellarView: React.FC<CellarViewProps> = ({ wines, onRemove, onUpdateQty, onUpdateWine, onViewToggle }) => {
  const [filter, setFilter] = useState<WineType | 'Tutti'>('Tutti');
  const [sort, setSort] = useState<'valore' | 'recente' | 'nome' | 'beva'>('recente');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWineId, setSelectedWineId] = useState<string | null>(null);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { id: 'recente', label: 'Più Recenti' },
    { id: 'beva', label: '⏳ Miglior Beva' },
    { id: 'valore', label: 'Valore (€)' },
    { id: 'nome', label: 'Alfabetico (A-Z)' }
  ];

  const filteredWines = (wines || [])
    .filter(w => {
      if (!w) return false;
      const matchesFilter = filter === 'Tutti' || String(w.type).toLowerCase() === String(filter).toLowerCase();
      const matchesSearch = 
        w.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        w.producer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (w.grapes && w.grapes.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      if (sort === 'valore') return (Number(b.price) || 0) - (Number(a.price) || 0);
      if (sort === 'nome') return (a.name || "").localeCompare(b.name || "");
      if (sort === 'beva') {
        const extractYear = (windowStr: string) => {
          const match = (windowStr || "").match(/\d{4}/);
          return match ? parseInt(match[0]) : 9999;
        };
        return extractYear(a.drinkingWindow) - extractYear(b.drinkingWindow);
      }
      return (Number(b.addedAt) || 0) - (Number(a.addedAt) || 0);
    });

  const handleSelectWine = (id: string | null) => {
    setSelectedWineId(id);
    onViewToggle(!!id);
    const mainContainer = document.querySelector('.ios-scroll-container');
    if (mainContainer) mainContainer.scrollTop = 0;
  };

  const selectedWine = wines.find(w => w.id === selectedWineId) || null;

  if (selectedWine) {
    return (
      <WineCard 
        wine={selectedWine} 
        onClose={() => handleSelectWine(null)} 
        onUpdateQty={onUpdateQty} 
        onRemove={onRemove} 
        onUpdateWine={onUpdateWine}
      />
    );
  }

  return (
    <div className="space-y-6 animate-scale-in">
      <div className="sticky top-0 z-30 bg-[#0a0503]/95 backdrop-blur-xl py-4 -mx-4 px-4 border-b border-white/10 shadow-2xl space-y-4">
        <div className="flex overflow-x-auto no-scrollbar gap-2">
          {['Tutti', WineType.RED, WineType.WHITE, WineType.ROSE, WineType.SPARKLING, WineType.DESSERT, WineType.SPIRITS].map(t => (
            <button 
              key={t} 
              onClick={() => setFilter(t as any)} 
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${filter === t ? 'bg-amber-600 text-white shadow-lg' : 'bg-white/5 text-slate-500'}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="relative">
          <input 
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca in cantina..."
            className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 px-10 text-xs text-amber-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-600/50"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
        </div>

        <div className="flex justify-between items-center px-1">
          <span className="text-[9px] font-black text-amber-500/60 uppercase tracking-[0.2em]">{filteredWines.length} Elementi</span>
          <div className="relative">
            <button 
              onClick={() => setIsSortOpen(!isSortOpen)}
              className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-full active:scale-95 transition-all"
            >
              <span className="text-[9px] font-black text-amber-500 uppercase tracking-widest">
                {sortOptions.find(o => o.id === sort)?.label}
              </span>
              <svg className={`text-amber-500/50 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            {isSortOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsSortOpen(false)} />
                <div className="absolute right-0 mt-2 w-44 bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden animate-scale-in py-1">
                  {sortOptions.map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => { setSort(opt.id as any); setIsSortOpen(false); }}
                      className={`w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest ${sort === opt.id ? 'bg-amber-600 text-white' : 'text-slate-400 active:bg-white/5'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pb-32">
        {filteredWines.map(wine => (
          <div 
            key={wine.id} 
            onClick={() => handleSelectWine(wine.id)}
            className="bg-white/[0.03] border border-white/5 rounded-[2rem] overflow-hidden flex flex-col group active:scale-95 transition-all shadow-xl"
          >
            <div className="aspect-[3/4] bg-white relative p-4 flex items-center justify-center">
              <img src={wine.imageUrls[0]} className="h-full w-full object-contain" alt={wine.name} />
              <div className="absolute top-3 left-3 flex flex-col gap-1">
                <span className="bg-black/90 text-white text-[7px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">{wine.type}</span>
                {wine.quantity > 1 && <span className="bg-amber-600 text-white text-[7px] font-black px-2 py-0.5 rounded-full w-fit">x{wine.quantity}</span>}
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <h3 className="text-[10px] font-black text-amber-100 uppercase tracking-widest line-clamp-2 leading-tight mb-1">{wine.name}</h3>
              <p className="text-[8px] text-slate-500 font-bold uppercase italic truncate mb-2">{wine.producer}</p>
              <div className="mt-auto pt-2 border-t border-white/5 flex justify-between items-center">
                <span className="text-[9px] font-black text-amber-500/80 tracking-widest">{wine.vintage}</span>
                <span className="text-[9px] font-black text-white/40">€{wine.price}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WineCard: React.FC<{ 
  wine: Wine; 
  onClose: () => void; 
  onUpdateQty: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
  onUpdateWine: (id: string, updates: Partial<Wine>) => void;
}> = ({ wine, onClose, onUpdateQty, onRemove, onUpdateWine }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Wine>>(wine);

  const handleDrink = () => {
    onUpdateQty(wine.id, -1);
    if (wine.quantity <= 1) {
      onClose();
    }
  };

  const handleSave = () => {
    onUpdateWine(wine.id, editData);
    setIsEditing(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0a0503] flex flex-col animate-scale-in">
      <div className="absolute top-0 left-0 right-0 z-[110] p-6 pt-[calc(env(safe-area-inset-top)+1rem)] flex justify-between items-center pointer-events-none">
        <button 
          onClick={onClose} 
          className="pointer-events-auto w-12 h-12 bg-black/80 backdrop-blur-2xl rounded-2xl flex items-center justify-center text-white active:scale-75 transition-all shadow-2xl border border-white/20"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <button 
          onClick={() => setIsEditing(true)} 
          className="pointer-events-auto w-12 h-12 bg-black/80 backdrop-blur-2xl rounded-2xl flex items-center justify-center text-amber-500 active:scale-75 transition-all shadow-2xl border border-white/20"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar ios-scroll-container">
        <div className="w-full bg-white flex items-center justify-center p-12 pt-28 min-h-[60vh]">
          <img src={wine.imageUrls[0]} className="max-h-[60vh] object-contain drop-shadow-2xl" alt={wine.name} />
        </div>

        <div className="p-8 pb-40 space-y-12">
          <header className="space-y-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                 <span className="text-[10px] font-black text-amber-600 uppercase tracking-[0.4em]">{wine.type}</span>
                 <span className="text-slate-700">•</span>
                 <span className="text-[10px] font-black text-amber-600 uppercase tracking-[0.4em]">{wine.vintage}</span>
              </div>
              <h2 className="text-4xl font-bold text-amber-500 serif leading-tight">{wine.name}</h2>
              <p className="text-sm text-slate-400 font-bold uppercase tracking-widest italic">{wine.producer}</p>
            </div>
            <div className="text-3xl font-bold text-white flex items-baseline gap-1">
               <span className="text-amber-500/40 text-sm">€</span>
               <span>{wine.price}</span>
            </div>
          </header>

          <div className="grid grid-cols-1 gap-4">
            <button 
              onClick={handleDrink}
              className="w-full bg-amber-600 text-white font-black py-5 rounded-[2rem] flex items-center justify-center gap-3 shadow-[0_10px_30px_-5px_rgba(217,119,6,0.4)] active:scale-95 transition-all uppercase tracking-widest text-xs"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              Stappa e Bevi
            </button>
            
            <div className="flex gap-4">
              <div className="flex-1 bg-white/[0.03] p-6 rounded-3xl border border-white/5">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest block mb-2">In Stock</span>
                <div className="flex items-center justify-between">
                  <button onClick={() => onUpdateQty(wine.id, -1)} className="text-amber-500 p-2 active:scale-75"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                  <span className="text-2xl font-bold text-white">{wine.quantity}</span>
                  <button onClick={() => onUpdateQty(wine.id, 1)} className="text-amber-500 p-2 active:scale-75"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                </div>
              </div>
              <div className="flex-1 bg-white/[0.03] p-6 rounded-3xl border border-white/5 text-center flex flex-col justify-center">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest block mb-1">Miglior Beva</span>
                <p className="text-amber-100 font-bold text-lg">{wine.drinkingWindow}</p>
              </div>
            </div>
          </div>

          <section className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Analisi Sensoriale (AI)</h4>
              <p className="text-amber-100/90 text-lg leading-relaxed font-serif italic">"{wine.description || 'Nessuna analisi disponibile.'}"</p>
              <div className="pt-4 space-y-2 border-t border-white/5">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest block">Uvaggio</span>
                <p className="text-sm text-slate-300 font-bold tracking-wide">{wine.grapes || 'Varietà non specificata'}</p>
              </div>
            </div>

            <div className="bg-amber-900/10 border border-amber-900/20 p-7 rounded-[2.5rem] space-y-4 shadow-inner">
               <div className="flex items-center gap-3 text-amber-500">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em]">Note di Degustazione</h4>
               </div>
               <p className="text-sm text-amber-100/80 font-serif leading-relaxed italic min-h-[4rem]">
                 {wine.review || "Nessun appunto ancora scritto. Registra qui le tue sensazioni dopo la bevuta."}
               </p>
            </div>

            <div className="bg-white/[0.02] border border-white/5 p-7 rounded-[2.5rem] space-y-4 shadow-sm">
               <div className="flex items-center gap-3 text-slate-500">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em]">Note di Cantina</h4>
               </div>
               <p className="text-[11px] text-slate-400 font-bold leading-relaxed min-h-[3rem]">
                 {wine.notes || "Note tecniche (es. posizione scaffale, fornitore, prezzo d'acquisto)."}
               </p>
            </div>
          </section>

          <section className="space-y-4">
             <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Abbinamenti Suggeriti</h4>
             <div className="flex flex-wrap gap-2">
               {wine.foodPairings?.length > 0 ? wine.foodPairings.map((food, i) => (
                 <span key={i} className="bg-white/5 border border-white/5 px-4 py-3 rounded-2xl text-[10px] text-slate-400 font-black uppercase tracking-widest">{food}</span>
               )) : <span className="text-[10px] text-slate-600 italic uppercase tracking-widest">In cerca di ispirazione...</span>}
             </div>
          </section>

          <button 
            onClick={() => { if(confirm("Vuoi rimuovere definitivamente questa referenza?")) { onRemove(wine.id); onClose(); } }}
            className="w-full text-red-500/20 text-[9px] font-black uppercase tracking-[0.3em] py-12 active:text-red-500 transition-colors"
          >
            Elimina Referenza
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="fixed inset-0 z-[120] bg-[#0a0503] flex flex-col p-8 space-y-6 overflow-y-auto ios-scroll-container">
          <header className="flex justify-between items-center mb-4 pt-[calc(env(safe-area-inset-top))]">
            <h3 className="text-2xl font-bold text-amber-500 serif">Modifica Dati</h3>
            <button onClick={() => setIsEditing(false)} className="bg-white/5 p-3 rounded-2xl text-slate-500 active:scale-75"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>
          </header>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Nome Etichetta</label>
              <input value={editData.name || ''} onChange={e => setEditData({...editData, name: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-amber-100 focus:outline-none focus:border-amber-600/50" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Vendemmia</label>
                <input value={editData.vintage || ''} onChange={e => setEditData({...editData, vintage: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-amber-100 focus:outline-none focus:border-amber-600/50" />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Prezzo (€)</label>
                <input type="number" value={editData.price || 0} onChange={e => setEditData({...editData, price: Number(e.target.value)})} className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-amber-100 focus:outline-none focus:border-amber-600/50" />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Note di Degustazione</label>
              <textarea value={editData.review || ''} onChange={e => setEditData({...editData, review: e.target.value})} placeholder="Sapore, profumi, emozioni dopo averlo bevuto..." className="w-full bg-white/5 border border-white/10 rounded-3xl p-6 text-sm text-amber-100 focus:outline-none focus:border-amber-600/50 h-32 resize-none" />
            </div>

            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Note Tecniche (Cantina)</label>
              <textarea value={editData.notes || ''} onChange={e => setEditData({...editData, notes: e.target.value})} placeholder="Posizione scaffale, fornitore, lotto, note varie..." className="w-full bg-white/5 border border-white/10 rounded-3xl p-6 text-sm text-amber-100 focus:outline-none focus:border-amber-600/50 h-32 resize-none" />
            </div>

            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Analisi IA (Modificabile)</label>
              <textarea value={editData.description || ''} onChange={e => setEditData({...editData, description: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-3xl p-6 text-sm text-slate-400 focus:outline-none focus:border-amber-600/50 h-40 resize-none" />
            </div>
          </div>
          
          <button onClick={handleSave} className="w-full bg-amber-600 text-white font-black py-6 rounded-[2rem] uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all mt-8">Salva Modifiche</button>
          <div className="h-20" />
        </div>
      )}
    </div>
  );
};

export default CellarView;
